import React, { useRef } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { radioSelect_issueFieldRadioSelectReadviewFull_RadioSelectReadView$key as RadioSelectFragment } from './__generated__/radioSelect_issueFieldRadioSelectReadviewFull_RadioSelectReadView.graphql';
import type { RadioSelectReadViewProps } from './types.tsx';

/**
 * The RadioSelectReadView will show a read only view of RadioSelect field.
 * @param props [RadioSelectReadViewProps](./types.tsx)
 */
export const RadioSelectReadView = ({
	fragmentRef,
	maxLines,
	shouldRenderHoverPopover,
}: RadioSelectReadViewProps): React.JSX.Element => {
	const data = useFragment<RadioSelectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment radioSelect_issueFieldRadioSelectReadviewFull_RadioSelectReadView on JiraRadioSelectField {
				id
				selectedOption {
					label: value
				}
			}
		`,
		fragmentRef,
	);
	const { selectedOption } = data;

	const contentRef = useRef(null);

	const hoverPopoverRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	if (!selectedOption?.label) {
		return <EmptyFieldText />;
	}

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: selectedOption?.label,
		});
	}
	if (shouldRenderHoverPopover) {
		return (
			<Text maxLines={maxLines} ref={hoverPopoverRef}>
				{selectedOption?.label}
			</Text>
		);
	}

	return (
		<Text maxLines={maxLines} ref={contentRef}>
			{selectedOption?.label}
		</Text>
	);
};
